/** 指挥页常量（从 Dashboard.js 拆出） */
/**
 * 方案3：指挥页三态由 useMemberPresenceTracker 按 last_seen 静默时长驱动（约 5 秒重算）。
 * - 绿：静默 < 暂时离线阈值
 * - 橙：暂时离线 ≤ 静默 < 正式离线
 * - 灰：静默 ≥ 正式离线
 * 不增加队员端上报频率 / 耗电。
 */
/** 暂时离线（橙灯）默认分钟数；管理后台 member_temp_offline_minutes / 兼容 member_online_minutes */
export const DEFAULT_TEMP_OFFLINE_MINUTES = 5;
export const DEFAULT_ONLINE_TIMEOUT_MINUTES = DEFAULT_TEMP_OFFLINE_MINUTES;
export const ONLINE_TIMEOUT = DEFAULT_TEMP_OFFLINE_MINUTES * 60 * 1000;
/** 正式离线（灰灯）默认：管理后台 member_offline_minutes */
export const DEFAULT_OFFLINE_TIMEOUT_MINUTES = 15;
export const DEFAULT_OFFLINE_TIMEOUT = DEFAULT_OFFLINE_TIMEOUT_MINUTES * 60 * 1000;

export const PRESENCE_STATUS = {
  ONLINE: 'online',
  TEMP_OFFLINE: 'temp_offline',
  OFFLINE: 'offline'
};

export const APP_PIN_COLORS = {
  active: '#e23b3b',
  highlighted: '#2b5fd0',
  online: '#2ea8ff',
  tempOffline: '#f59e0b',
  offline: '#8a93a0'
};

export const LOCUS_MARKER_ICONS = {
  active: '/marker-red.png',
  highlighted: '/marker-darkblue.png',
  online: '/marker-lightblue.png',
  offline: '/marker-gray.png'
};

export const PRESENCE_DOT_COLORS = {
  online: '#2ea8ff',
  temp_offline: '#f59e0b',
  offline: '#8a93a0'
};

export const PLAYBACK_STEP_MS = 800;

/**
 * 指挥页顶栏主模式。混合按钮可下拉高程/地形；刷新或换图源后仍显示二维/卫星/混合。
 */
export const DASHBOARD_MAP_STYLES = [
  { name: '二维地图', id: 'streets' },
  { name: '卫星地图', id: 'satellite' },
  { name: '混合地图', id: 'hybrid' }
];

export const DASHBOARD_MAP_EXTRA_STYLES = [
  { name: '高程地图', id: 'outdoors' },
  { name: '地形图', id: 'terrain' }
];

/** 实时轨迹线宽等默认 paint；line-color 由 resolveTrackColor 按队员覆盖 */
export const TRACK_LINE_PAINT = {
  'line-color': '#ff0000',
  'line-width': 3
};

export const DASHBOARD_STYLES = `
  @keyframes voicePulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.25); opacity: 0.55; }
    100% { transform: scale(1); opacity: 1; }
  }
  /* 语音未读：三道弯曲音波颜色轮换（非整体缩放） */
  @keyframes voiceWaveColorCycle {
    0% { stroke: #90a4ae; }
    25% { stroke: #1976d2; }
    50% { stroke: #42a5f5; }
    75% { stroke: #1565c0; }
    100% { stroke: #90a4ae; }
  }
  @keyframes voiceDotColorCycle {
    0%, 100% { fill: #90a4ae; }
    50% { fill: #1976d2; }
  }
  .voice-waves-unread .voice-wave-1 {
    animation: voiceWaveColorCycle 1.2s ease-in-out infinite;
  }
  .voice-waves-unread .voice-wave-2 {
    animation: voiceWaveColorCycle 1.2s ease-in-out 0.2s infinite;
  }
  .voice-waves-unread .voice-wave-3 {
    animation: voiceWaveColorCycle 1.2s ease-in-out 0.4s infinite;
  }
  .voice-waves-unread .voice-wave-dot {
    animation: voiceDotColorCycle 1.2s ease-in-out infinite;
  }


  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* 手机端拖拽容器动画 */
  .mobile-container {
    transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .mobile-container.dragging {
    transition: none;
  }
  
  /* 拖拽条悬停效果 */
  .drag-handle:hover {
    background: linear-gradient(135deg, #1565c0, #1976d2) !important;
  }
  
  .drag-handle:active {
    background: linear-gradient(135deg, #0d47a1, #1565c0) !important;
    cursor: grabbing !important;
  }
  
  /* 地图容器动画 */
  .map-container {
    transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  /* 上线公告动画 */
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideInTop {
    from {
      opacity: 0;
      transform: translateY(-100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes pulse {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.1);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  
  /* 右侧面板动画 */
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideOutRight {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(100%);
    }
  }
  
  .left-panel {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .left-panel.visible {
    animation: slideInRight 0.3s ease-out;
  }
  
  .left-panel.hidden {
    animation: slideOutRight 0.3s ease-in;
  }
`;
